/**
 * @description Convert a subscription package to an activation bundle.
 * The only difference is the type of the bundle, it will be inherited from SubscriptionPackageModel.
 * @param subscriptionPackage - The subscription package to convert.
 * @param forceDisabled - If the bundle should be disabled.
 * @returns The converted activation bundle.
 */
const convertSubscription = (subscriptionPackage: ApiSchema<'SubscriptionPackageModel'>, forceDisabled: boolean = false): ApiSchema<'ActivationBundle'> => {
  const benefits = subscriptionPackage?.benefits?.map((benefit) => ({ title: benefit }));
  return {
    packageId: subscriptionPackage?.packageId,
    selected: forceDisabled ? false : subscriptionPackage?.selected || false,
    recommended: forceDisabled ? false : subscriptionPackage?.recommended || false,
    activationPeriodInfo: subscriptionPackage.activationPeriodInfo,
    selectable: forceDisabled ? false : subscriptionPackage?.selectable || false,
    repostingCompatible: subscriptionPackage?.repostingCompatible || false,
    maxLocations: subscriptionPackage?.maxLocations || 0,
    title: 'global.label.freemium',
    subtitle: subscriptionPackage.subtitle,
    label: subscriptionPackage.label,
    credits: 0,
    discountedCredits: 0,
    available: forceDisabled ? false : subscriptionPackage?.available || false,
    current: forceDisabled ? false : subscriptionPackage?.current || false,
    unavailableReason: subscriptionPackage?.unavailableReason,
    hire: false,
    type: subscriptionPackage.type as ApiSchema<'ActivationBundle'>['type'],
    benefits: benefits || [],
    identifier: 'premium-subscription' as ApiSchema<'ActivationBundle'>['identifier'],
  };
};

/**
 * @description Merge the data from the activation services two-steps API - v3 (bundles & subscription package).
 * @param data - The data from the activation services two-steps API - v3.
 * @returns The merged data.
 */
export const mergeData = async (data: Awaited<ApiSchema<'ActivationServices2'>> | undefined) => {
  const bundles = data?.bundles;
  const subscriptionPackage = data?.subscriptionPackage;
  if (bundles && subscriptionPackage) {
    return bundles.concat([convertSubscription(subscriptionPackage)]);
  }
  return undefined;
};

/**
 * @description Get the initial index for job activation items.
 * @param jobActivationItems - The job activation items (bundles + converted subscriptionPackage).
 * @param activeFirst - If the active item should be first.
 * @returns The initial index.
 */
export const initialActivationIndex = (jobActivationItems: ApiSchema<'ActivationBundle'>[] | undefined, activeFirst = true): number => {
  // Initial selected index (for add)
  const initialSelectedIndex = jobActivationItems?.findIndex(
    (item) => item?.selected && item?.available,
  );

  // Initial recommended index (prefer when not in edit/active mode)
  const recommendedAvailableIndex = jobActivationItems?.findIndex(
    (item) => item?.recommended && item?.available,
  );

  // Initial active index (for upgrade and extend)
  const initialActiveIndex = jobActivationItems?.findIndex(
    (item) => item?.current && item?.available,
  );

  // Fallback: find the first available index
  const fallbackAvailableIndex = jobActivationItems?.findIndex(
    (item) => item?.available,
  );

  // Indexes array
  const indexes = activeFirst
    // In active/edit context, keep previously selected first (persist user's choice), then current, then recommended, then any available
    ? [initialSelectedIndex, initialActiveIndex, recommendedAvailableIndex, fallbackAvailableIndex]
    // In non-active (add) context, prefer recommended first, then previously selected, then current, then any available
    : [recommendedAvailableIndex, initialSelectedIndex, initialActiveIndex, fallbackAvailableIndex];

  // Now get the initial index
  return indexes.find((index) => index !== -1) || 0;
};

/**
 * @description Get the selected activation.
 * @param jobActivationItems - The job activation items (bundles + converted subscriptionPackage).
 * @param activeFirst - If the active item should be first.
 */
export const getSelectedActivation = (jobActivationItems: ApiSchema<'ActivationBundle'>[] | undefined, activeFirst = true) => {
  const selectedItem = jobActivationItems && jobActivationItems.length > 0
    ? jobActivationItems?.[initialActivationIndex(jobActivationItems, activeFirst)]
    : undefined;
  return selectedItem;
};
